import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A horizontal violin plot is a variation of the vertical violin plot, where the data
          distribution is displayed along the horizontal axis. It combines aspects of a box plot and
          a kernel density plot, showing the spread and density of data at different values. The
          width of the &rdquo;violin&rdquo; indicates the data density, with the box and median line
          offering insights into the central tendency and variability of the data. It&apos;s
          particularly useful for comparing distributions across multiple categories when a
          horizontal layout is preferred.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Horizontal violin plots are powerful tools for analyzing and comparing data distributions,
          presenting detailed insights in a compact format. Here are three significant uses:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Visualizing Full Data Distribution:</strong> They effectively display the
            density and spread of data across a horizontal axis, making it easy to identify patterns
            and variations.
          </li>
          <li>
            <strong>Comparative Analysis Across Groups:</strong> Horizontal orientation is
            particularly useful when comparing multiple categories, especially when category labels
            are lengthy.
          </li>
          <li>
            <strong>Highlighting Data Skewness and Modes:</strong> Their shape captures asymmetries,
            multimodal patterns, and other distribution characteristics at a glance.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Horizontal violin plots are an effective tool for visualizing the distribution of data
          across categories, but they also come with some limitations:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Space for Many Categories:</strong> When dealing with a large number of
            categories, horizontal violin plots can become cramped, leading to overlapping and
            making it difficult to distinguish between the different distributions. This can
            negatively impact readability and clarity.
          </li>
          <li>
            <strong>Complexity for Unfamiliar Audiences:</strong> Due to the combination of box plot
            and density plot elements, horizontal violin plots can be challenging for individuals
            who are not familiar with this type of chart. Understanding the nuances of distribution,
            quartiles, and density curves may require a higher level of statistical literacy.
          </li>
          <li>
            <strong>Skewed Visual Representation:</strong> If there are extreme outliers or
            imbalanced data within categories, the width of the violin plots can become misleading.
            In some cases, these outliers may cause the plot to appear more significant than they
            actually are, leading to potential misinterpretations of the data.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection in horizontal violin plots is key to enhancing data clarity and visual
          appeal. By applying distinct colors to different categories, you can emphasize differences
          in data distribution across groups. Using gradient shades can show intensity or
          concentration of values, with lighter tones indicating lower frequencies and darker hues
          representing higher densities. Additionally, contrasting colors for elements such as the
          median or quartiles help focus attention on important insights. Careful color choices
          improve legibility and prevent the chart from becoming visually overwhelming, making it
          more accessible and easier to interpret.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['diverging']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Horizontal violin plots offer a unique perspective on data distributions, yet they pose
          certain challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Space Utilization:</strong> Horizontal alignment can require significant
            horizontal space, especially with numerous categories, making the chart less suitable
            for compact layouts.
          </li>
          <li>
            <strong>Clarity in Overlapping Data:</strong> Similar to vertical violin plots,
            overlapping distributions can become hard to distinguish, reducing the chart&apos;s
            readability when many categories are present.
          </li>
          <li>
            <strong>Orientation Interpretation:</strong> Users accustomed to vertical data
            representations may find horizontal orientation less intuitive, requiring additional
            effort to interpret the data effectively.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
